/**
 * useOfflineSync.js
 * Custom hook: loads the offline save cache from localStorage,
 * persists changes, and periodically syncs pending items to the backend.
 */
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  saveParentField,
  saveChildField,
  loadOfflineCache,
  saveOfflineCache,
} from './serverApi';

export function useOfflineSync() {
  const [offlineSaves, setOfflineSaves] = useState({});

  // Hydrate from localStorage on mount
  useEffect(() => {
    setOfflineSaves(loadOfflineCache());
  }, []);

  // Persist any new changes to localStorage
  useEffect(() => {
    saveOfflineCache(offlineSaves);
  }, [offlineSaves]);

  // Periodic background sync every 15 s
  useEffect(() => {
    const sync = async () => {
      const keys = Object.keys(offlineSaves);
      if (keys.length === 0) return;

      const remaining = { ...offlineSaves };
      let syncedAny = false;

      for (const key of keys) {
        const item = offlineSaves[key];
        try {
          if (item.type === 'parent') {
            const res = await saveParentField(item.id, item.field, item.value);
            if (!res.data?.status) throw new Error('save failed');
          } else {
            const res = await saveChildField(item.table, item.serverId, item.field, item.value);
            if (!res.data?.status) throw new Error('save failed');
          }
          delete remaining[key];
          syncedAny = true;
        } catch {
          // Keep item in cache; will retry next cycle
        }
      }

      if (syncedAny) {
        setOfflineSaves(remaining);
        toast.success('Offline changes synchronized!');
      }
    };

    const id = setInterval(sync, 15_000);
    return () => clearInterval(id);
  }, [offlineSaves]);

  /** Queue a parent-row field save, with optimistic update callback */
  const queueParentSave = useCallback(
    async (id, field, value, onOptimisticUpdate) => {
      onOptimisticUpdate?.();
      const cacheKey = `parent_${id}_${field}`;
      try {
        const res = await saveParentField(id, field, value);
        if (!res.data?.status) throw new Error('failed');

        // Remove from cache if it was offline-queued previously
        setOfflineSaves((prev) => {
          const next = { ...prev };
          delete next[cacheKey];
          return next;
        });
        toast.success('Saved successfully');
        return { ok: true };
      } catch {
        const entry = { type: 'parent', id, field, value, ts: Date.now() };
        setOfflineSaves((prev) => ({ ...prev, [cacheKey]: entry }));
        toast.warning('Offline: change cached locally');
        return { ok: false };
      }
    },
    []
  );

  /** Queue a child-table field save */
  const queueChildSave = useCallback(async (serverId, table, field, value) => {
    const cacheKey = `child_${serverId}_${table}_${field}`;
    try {
      const res = await saveChildField(table, serverId, field, value);
      if (!res.data?.status) throw new Error('failed');

      setOfflineSaves((prev) => {
        const next = { ...prev };
        delete next[cacheKey];
        return next;
      });
      toast.success('Saved successfully');
      return { ok: true };
    } catch {
      const entry = { type: 'child', serverId, table, field, value, ts: Date.now() };
      setOfflineSaves((prev) => ({ ...prev, [cacheKey]: entry }));
      toast.warning('Offline: change cached locally');
      return { ok: false };
    }
  }, []);

  /** True if any pending offline saves exist for a given server id */
  const hasPending = useCallback(
    (serverId) =>
      Object.keys(offlineSaves).some((k) => k.includes(`_${serverId}_`)),
    [offlineSaves]
  );

  return { offlineSaves, queueParentSave, queueChildSave, hasPending };
}
